import React, { useMemo } from 'react';
import styles from './AdminUserForm.module.scss';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import { FormField } from '@tcc-online/input-components';
import locales from '../../../../config/locales.json';
import ILocale from '../../../../types/ILocale';
import { NumberSchema, ObjectSchema, StringSchema } from 'yup';
import { useTranslate } from 'react-translate.ts';
import AdminFormInput from '../../input/AdminFormInput';
import AdminFormSelect from '../../input/AdminFormSelect';
import UserTypes from '../../../../common/constants/UserTypes';
import useDebounce from '../../../../helpers/useDebounce';
import AdminButton from '../../buttons/AdminButton/AdminButton';
import { ButtonVariants } from '@tcc-online/common-components';

type AdminUserFormProps = {
    defaultValues?: AdminUserFormValues;
    onSubmit: (value: AdminUserFormValues) => void;
    innerRef?: React.MutableRefObject<FormikProps<AdminUserFormValues>>;
    title?: string;
    async?: boolean;
};

const EMAIL_FIELD = 'email';
const TYPE_FIELD = 'type';
const LANG_FIELD = 'language';
const FIRSTN_FIELD = 'firstName';
const LASTN_FIELD = 'lastName';

export type AdminUserFormValues = {
    [EMAIL_FIELD]: string;
    [TYPE_FIELD]: typeof UserTypes.User | typeof UserTypes.Admin;
    [LANG_FIELD]: string | null;
    [FIRSTN_FIELD]: string;
    [LASTN_FIELD]: string;
};

const initialValues: AdminUserFormValues = {
    [EMAIL_FIELD]: '',
    [TYPE_FIELD]: UserTypes.User,
    [LANG_FIELD]: null,
    [FIRSTN_FIELD]: '',
    [LASTN_FIELD]: '',
};

const languageOptions = Object.entries(locales).map(
    (entry: [string, ILocale]) => {
        const [key, locale] = entry;
        return { value: key, label: locale.name };
    },
);

export default function AdminUserForm(props: AdminUserFormProps) {
    const { title, defaultValues, onSubmit, innerRef, async = false } = props;
    const translate = useTranslate();

    return (
        <Formik
            innerRef={innerRef}
            initialValues={
                defaultValues
                    ? { ...initialValues, ...defaultValues }
                    : initialValues
            }
            onSubmit={onSubmit}
            validationSchema={getValidationSchema}
        >
            <FormContent title={title} async={async} />
        </Formik>
    );

    function getValidationSchema() {
        return new ObjectSchema({
            [EMAIL_FIELD]: new StringSchema().required(
                translate('fe.form.required', {
                    0: translate('fe.user.email'),
                }),
            ),
            [FIRSTN_FIELD]: new StringSchema().nullable(),
            [LASTN_FIELD]: new StringSchema().nullable(),
            [TYPE_FIELD]: new NumberSchema().required(
                translate('fe.form.required', {
                    0: translate('Type'), // TODO: translate
                }),
            ),
            [LANG_FIELD]: new StringSchema().nullable(),
        });
    }
}

interface FormProps {
    async: boolean;
    title?: string;
}

function FormContent(props: FormProps) {
    const { title, async } = props;
    const translate = useTranslate();
    const formik = useFormikContext();
    const debounce = useDebounce();
    const typeOptions = useMemo(
        () => [
            { value: 1, label: translate('ucastnik') },
            { value: 2, label: translate('spravce') },
        ],
        [translate],
    );

    return (
        <Form className={styles.form}>
            {title && <h3>{title}</h3>}
            <FormField>
                <AdminFormInput
                    name={EMAIL_FIELD}
                    placeholder={translate('Email')}
                    onChange={handleInputChange(EMAIL_FIELD, async)}
                />
            </FormField>
            <FormField>
                <AdminFormSelect
                    name={TYPE_FIELD}
                    options={typeOptions}
                    placeholder={translate('Typ')}
                    onChange={handleSelectChange(TYPE_FIELD, async)}
                />
            </FormField>
            <FormField>
                <AdminFormInput
                    name={FIRSTN_FIELD}
                    placeholder={translate('Jmeno')}
                    onChange={handleInputChange(EMAIL_FIELD, async)}
                />
            </FormField>
            <FormField>
                <AdminFormInput
                    name={LASTN_FIELD}
                    placeholder={translate('Prijmeni')}
                    onChange={handleInputChange(EMAIL_FIELD, async)}
                />
            </FormField>
            <FormField>
                <AdminFormSelect
                    name={LANG_FIELD}
                    options={languageOptions}
                    placeholder={translate('Jazyk')}
                    onChange={handleSelectChange(TYPE_FIELD, async)}
                    searchable
                />
            </FormField>
            {!async && (
                <AdminButton
                    variant={ButtonVariants.positive}
                    type="submit"
                    className={styles.save}
                >
                    {translate('Ulozit')}
                    {/*  TODO: translate  */}
                </AdminButton>
            )}
        </Form>
    );

    function handleInputChange(name: string, async: boolean) {
        return async (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            await formik.setFieldValue(name, value);

            if (async) {
                await debounce(formik.submitForm);
            }
        };
    }

    function handleSelectChange(name: string, async: boolean) {
        return async (value: any) => {
            await formik.setFieldValue(name, value);

            if (async) {
                await formik.submitForm();
            }
        };
    }
}
